import {NoteIcon} from '@/modules/patients-therapies/components/note-badge'
import {formatTreatmentLabels} from '@/modules/patients-therapies/functions'
import {formatGfr, getLatestGfrValue, getPatientFullName} from '@/modules/patients/functions'
import {useGetSettings} from '@/modules/settings/api'
import {getFullName} from '@/modules/users/functions'
import {CopyButton} from '@/shared/components/ui/copy-button'
import {PatientTag} from '@/shared/components/ui/tags/patient-tag'
import {usePatientTherapySequenceContext} from '@/shared/contexts/patient-therapy-sequence-context'
import {useCan, usePermissions} from '@/shared/hooks/use-permissions'
import {Avatar, Badge, Box, BoxProps, Button, HStack, Span, Text, useBreakpointValue, VStack} from '@chakra-ui/react'
import {DoseCalcModeActive} from '@cypro/engine/database/schema'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {DrawerCloseTrigger, Tooltip} from '@cypro/ui/components'
import {getAge, getShortDateFormatter, multipleDigitsNumberFormatter, isDefined} from '@cypro/ui/utils'
import {TFnType, useTranslate} from '@tolgee/react'
import {type NoteKind} from './patient-therapy-sequence-drawer'
import {StatusBadge} from './status-badge'
import {PatientTherapySequenceTabsList} from './tabs-list'
import {Link} from '@tanstack/react-router'

export function PatientTherapySequenceSummary({
  onOpenNote,
  openNote,
}: {
  onOpenNote?: (kind: NoteKind) => void
  openNote?: NoteKind | null
}) {
  const {t} = useTranslate('common')
  const {sequence, responsibleDoctor} = usePatientTherapySequenceContext()
  const {settings} = useGetSettings()
  const isMobile = useBreakpointValue({base: true, md: false}) ?? false
  const patient = sequence.patient
  const hospitalPatientId = (settings?.showHospitalPatientId ?? true) ? patient?.hospitalPatientId : undefined
  const {can} = usePermissions()
  // doctors write the pharmacy note in the prescription dialog, so anyone who may edit a prescription sees it too
  const canSeePharmacyNotes = can('prescriptions.edit') || can('production.access')
  const canEditPatient = useCan('patients.edit')
  // the header always reads the active substance — the printed label does too and the
  // trade name must not make the line flip after pharmacy validation
  const treatmentLabels = formatTreatmentLabels(sequence, t, true)
  const birthDate = isDefined(patient?.birthDate) ? new Date(patient.birthDate) : undefined
  const diagnosis = sequence.protocol?.diagnosis?.name

  return (
    <Box display="flex" flexDir="column" gapY="2.5" width="100%">
      <HStack alignItems="flex-start" justifyContent="space-between" gap="3">
        <HStack alignItems="flex-start">
          {patient && (
            <Link
              to="/patient-dashboard/patients/$patientId"
              params={{patientId: patient.id}}
              search={{tab: 'therapy'}}
            >
              <Avatar.Root size="sm" flexShrink={0}>
                <Avatar.Fallback name={getPatientFullName(patient)} />
              </Avatar.Root>
            </Link>
          )}

          <Box>
            <Box minWidth="0" flex="1">
              <PatientTag patient={patient} behavior="open-record">
                <Text textStyle="md" fontWeight="medium" wordBreak="break-word">
                  {getPatientFullName(patient)}
                </Text>
              </PatientTag>
            </Box>

            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              columnGap="4"
              rowGap="0"
              color="fg.muted"
              fontSize="sm"
            >
              {birthDate && (
                <Text>
                  {t('dob')}: {getShortDateFormatter().format(birthDate)} ({getAge(birthDate.getTime())} {t('yrs')})
                </Text>
              )}

              {patient?.gender && <Text>{t(patient.gender)}</Text>}

              {hospitalPatientId && (
                <HStack gap="2" minWidth="0">
                  <Text wordBreak="break-word">
                    {t('hospital_id')}: <Span color="fg">{hospitalPatientId}</Span>
                  </Text>
                  <CopyButton value={hospitalPatientId} height="max-content" minWidth="max-content" />
                </HStack>
              )}

              {diagnosis && (
                <Text wordBreak="break-word" color="fg.muted">
                  {diagnosis}
                </Text>
              )}
            </Box>
          </Box>
        </HStack>

        <HStack flexShrink={0} gap="1">
          <StatusBadge sequence={sequence} />

          <DrawerCloseTrigger position="static" variant="ghost" />
        </HStack>
      </HStack>

      {sequence.internalId && (
        <HStack gap="1" fontSize="sm">
          <Text color="fg.muted">{t('prescription_number')}:</Text>
          <Text fontWeight="medium" whiteSpace="nowrap">
            {sequence.internalId}
          </Text>
          <CopyButton value={sequence.internalId} />
        </HStack>
      )}

      {treatmentLabels.medicines?.length ? (
        <Text fontSize="sm" fontWeight="medium" lineClamp={isMobile ? 2 : undefined}>
          {treatmentLabels.medicines.map(m => `${m.name} ${m.dosage}`).join(', ')}{' '}
          {treatmentLabels.carrierDetails.join(' ')}
        </Text>
      ) : (
        <Text fontSize="sm" color="fg.muted">
          {t('no_medication')}
        </Text>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
        <HStack gapX="2" alignItems="flex-start">
          {getVitals(sequence, t).map(vital => (
            <VitalChip key={vital.label} label={vital.label} value={vital.value} highlight={vital.highlight} />
          ))}
        </HStack>

        {responsibleDoctor && (
          <VStack textAlign="right" whiteSpace="nowrap" gapY="0" alignItems="flex-end">
            <Text textStyle="sm" fontWeight="medium">
              {getFullName(responsibleDoctor, 'last-first')}
            </Text>
            <Text textStyle="sm" color="fg.muted">
              {responsibleDoctor.userAccessLocations?.[0]?.ward?.name ||
                responsibleDoctor.userAccessLocations?.[0]?.section?.name ||
                null}
            </Text>
          </VStack>
        )}

        {/* <HStack gapX="1.5">
            {patient && !isMobile && (
              <>
                <Tooltip content={t('view_patient_profile')}>
                  <Link
                    to="/patient-dashboard/patients/$patientId"
                    params={{patientId: patient.id}}
                    search={{tab: 'therapy'}}
                    target="_blank"
                  >
                    <IconButton size="xs" variant="subtle" aria-label={t('view_patient_profile')}>
                      <LuExternalLink />
                    </IconButton>
                  </Link>
                </Tooltip>
                <Tooltip content={t('view_therapy_popup')}>
                  <IconButton
                    size="xs"
                    variant="subtle"
                    aria-label={t('view_therapy_popup')}
                    onClick={() => openPatientTherapyDialog(patient.id)}
                  >
                    <LuTablets />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </HStack> */}
      </Box>

      <HStack justifyContent="space-between">
        <PatientTherapySequenceTabsList />

        <HStack>
          {canEditPatient && (
            <NoteButton
              kind="therapy"
              label={t('prescription_notes')}
              note={sequence.therapyNote || sequence.note}
              isOpen={openNote === 'therapy'}
              onOpen={onOpenNote}
            />
          )}
          {canSeePharmacyNotes && (
            <NoteButton
              kind="pharmacy"
              label={t('pharmacy_notes')}
              note={sequence.pharmacyNote}
              isOpen={openNote === 'pharmacy'}
              onOpen={onOpenNote}
            />
          )}
        </HStack>
      </HStack>
    </Box>
  )
}

interface NoteButtonProps {
  kind: NoteKind
  label: string
  note: string | null
  isOpen?: boolean
  onOpen?: (kind: NoteKind) => void
}

// note badges toggle the orange alert at the top of the about tab;
// the badge is filled while its alert is open
function NoteButton({kind, label, note, isOpen, onOpen}: NoteButtonProps) {
  const {t} = useTranslate('activity')

  function getVariant() {
    if (isOpen) return 'solid'
    if (note) return 'outline'
    return 'subtle'
  }

  function getColorPalette() {
    if (isOpen) return 'orange'
    if (note) return 'yellow'
    return 'gray'
  }

  return (
    <Tooltip content={!note ? t('no_note') : null}>
      <Badge asChild variant={getVariant()} colorPalette={getColorPalette()}>
        <Button width="max-content" unstyled disabled={!note} onClick={() => note && onOpen?.(kind)}>
          {label}

          {note && <NoteIcon />}
        </Button>
      </Badge>
    </Tooltip>
  )
}

interface VitalChipProps extends BoxProps {
  label: string
  value: string
  highlight?: boolean
}

function VitalChip({label, value, highlight, ...props}: VitalChipProps) {
  return (
    <Box
      paddingX="2.5"
      paddingY="1.5"
      rounded="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="transparent"
      bg={highlight ? 'green.subtle' : 'bg.subtle'}
      {...props}
    >
      <Text fontSize="xs" color={highlight ? 'green.fg' : 'fg.muted'}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="medium" color={highlight ? 'green.fg' : undefined}>
        {value}
      </Text>
    </Box>
  )
}

interface Vital {
  label: string
  value: string
  highlight?: boolean
}

// each dose-calculation method derives the final dose from a specific patient
// characteristic (BSA, weight, or GFR-based AUC) — that characteristic's chip gets highlighted
function getDoseVitalKeys(sequence: PatientTherapySequence): string[] {
  return (sequence.medicine ?? []).flatMap(medicine => {
    switch (medicine.calculationMethod) {
      case DoseCalcModeActive.enum.BSA:
      case DoseCalcModeActive.enum.BSA30:
        return ['bsa']
      case DoseCalcModeActive.enum.WEIGHT:
        return ['weight']
      case DoseCalcModeActive.enum.AUC:
        return ['gfr']
      default:
        return []
    }
  })
}

// the values the dose was calculated with; prescriptions saved before they were recorded fall back to the patient's current values
function getDoseBasisVitals(sequence: PatientTherapySequence) {
  const {doseBasisHeight, doseBasisWeight, doseBasisBsa, doseBasisGfr, doseBasisAibw, doseBasisUseAibw} = sequence
  if (doseBasisHeight != null || doseBasisWeight != null || doseBasisBsa != null || doseBasisGfr != null) {
    return {
      height: doseBasisHeight,
      weight: doseBasisWeight,
      bsa: doseBasisBsa,
      gfr: doseBasisGfr,
      aibw: doseBasisAibw,
      useAibw: Boolean(doseBasisUseAibw),
    }
  }

  const characteristics = sequence.patient?.characteristics?.[0]
  return {
    height: characteristics?.height,
    weight: characteristics?.weight,
    bsa: characteristics?.bsa,
    gfr: getLatestGfrValue(sequence.patient),
    aibw: characteristics?.aibw,
    useAibw: Boolean(characteristics?.useAibw),
  }
}

function getVitals(sequence: PatientTherapySequence, t: TFnType): Vital[] {
  const {height, weight, bsa, gfr, aibw, useAibw} = getDoseBasisVitals(sequence)

  const doseVitalKeys = getDoseVitalKeys(sequence)

  return [
    {
      label: t('characteristics.height'),
      value: height ? `${multipleDigitsNumberFormatter.format(height)} cm` : '-',
    },
    {
      label: t('characteristics.weight'),
      value: weight ? `${multipleDigitsNumberFormatter.format(weight)} kg` : '-',
      highlight: !useAibw && doseVitalKeys.includes('weight'),
    },
    // weight-based doses used the AIBW instead of the weight
    ...(useAibw
      ? [
          {
            label: t('characteristics.aibw'),
            value: aibw ? `${multipleDigitsNumberFormatter.format(aibw)} kg` : '-',
            highlight: doseVitalKeys.includes('weight'),
          },
        ]
      : []),
    {
      label: t('characteristics.bsa'),
      value: bsa ? `${multipleDigitsNumberFormatter.format(bsa)} m²` : '-',
      highlight: doseVitalKeys.includes('bsa'),
    },
    {
      label: t('characteristics.gfr'),
      value: gfr ? `${formatGfr(gfr)} ml/min` : '-',
      highlight: doseVitalKeys.includes('gfr'),
    },
  ]
}
